import React, { useRef, useEffect, useCallback } from 'react'

/**
 * InfiniteScroll creates an invisible trigger that automatically loads more
 * content when users scroll near it.
 *
 * Put this component at the __bottom__ of your scrollable content list. It
 * uses IntersectionObserver to detect when the user has scrolled close enough
 * to warrant loading the next batch of items. The component handles all the
 * scroll detection logic and calls your provided callback when it's time to
 * fetch more data.
 *
 * The component is smart about when to trigger - it won't fire while you're
 * already loading data, and it automatically hides itself when there's nothing
 * left to load. Think of it as a "load more" button that activates itself based
 * on scroll position rather than user clicks.
 *
 * Typical usage: render your list items, then place this component after them
 * with a callback that fetches the next page of data and updates your item
 * count totals.
 */
interface InfiniteScrollProps {
  onLoadMore: () => void
  currentCount: number
  lastBatchSize: number
  limit: number
  loading?: boolean
  enabled?: boolean
  threshold?: number
  rootMargin?: string
  className?: string
  children?: React.ReactNode
}

export const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  onLoadMore,
  currentCount,
  lastBatchSize,
  limit,
  loading = false,
  enabled = true,
  threshold = 0.01,
  rootMargin = '0% 0% 50% 0%',
  className = '',
  children,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (loading || !enabled) {
        return
      }

      const isVisible = entries.some((entry) => entry.isIntersecting)
      if (isVisible) {
        onLoadMore()
      }
    },
    [onLoadMore, loading, enabled],
  )

  useEffect(() => {
    const trigger = triggerRef.current
    if (!trigger || !enabled || !window.IntersectionObserver) {
      return () => { }
    }

    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin,
      threshold,
    })

    observerRef.current.observe(trigger)

    return () => {
      if (observerRef.current && trigger) {
        observerRef.current.unobserve(trigger)
        observerRef.current = null
      }
    }
  }, [handleIntersection, threshold, rootMargin, enabled])

  // Hide infinite scroll if we got fewer items than the limit (indicating we've reached the end)
  // or if we have no items at all
  if (currentCount === 0 || lastBatchSize < limit) {
    return null
  }

  return (
    <div
      ref={triggerRef}
      className={`infinite-scroll ${className}`.trim()}
      style={{ height: '1px', opacity: 0 }}
    >
      {children}
    </div>
  )
}
